import AppDataService from "./AppDataService";

const COMMON_BASE = "auth";

export default class PatientListService {
  static async getParticipantsBySearch(searchKey, pageNumber, limit) {
    const queryParams = [
      searchKey ? `searchKey=${searchKey}` : "",
      pageNumber >= 0 ? `pageNumber=${pageNumber}` : "",
      limit ? `limit=${limit}` : "",
    ]
      .filter(Boolean)
      .join("&");

    const queryString = queryParams ? `?${queryParams}` : "";
    return await AppDataService.get(`participant${queryString}`);
  }

  static async createNewPatient(data) {
    return await AppDataService.post(`patient`, data);
  }

  static async getPatientList(data) {
    return await AppDataService.get(
      `patient?${data?.searchKey ? "searchKey=" + data?.searchKey : ""}${data?.pageNumber >= 0 ? "&pageNumber=" + data?.pageNumber : ""}${data?.limit ? "&limit=" + data?.limit : ""}${data?.allocated ? "&allocated=" + data?.allocated : ""}`,
    );
  }

  static async createNewVisit(data, key) {
    return await AppDataService.post(
      `visit${key ? "?force=" + key : ""}`,
      data,
    );
  }

  static async createEMSVisit(data) {
    return await AppDataService.post("visit/ems", data);
  }

  static async getClinicLocations() {
    return await AppDataService.get(`basecamp`);
  }

  static async createBaseCamp(data) {
    return await AppDataService.post(`basecamp`, data);
  }

  static async convertParticipantToPatient(data) {
    return await AppDataService.post(`patient`, data);
  }

  static async convertPartToPatient(data) {
    return await AppDataService.post(`patient/participant/convert`, data);
  }

  static async getPatientProfileData(id) {
    return await AppDataService.get(`patient/${id}`);
  }

  static async patchPatientProfileDetailsData(id, data) {
    return await AppDataService.patch(`patient/${id}`, data);
  }

  static async createTriage(data) {
    return await AppDataService.post(`triage`, data);
  }

  static async getPatientDetailsDataByTabWise(data) {
    return await AppDataService.get(data);
  }
  static async getVisitNoteById(id) {
    return await AppDataService.get(`visit/${id}`);
  }
  static async patchEmergencyContactData(id, data) {
    return await AppDataService.patch(`patient/${id}`, data);
  }
  static async patchBaseCampData(patientId, data) {
    return await AppDataService.patch(`patient/${patientId}/camp`, data);
  }
  static async patchChiefComplaintData(id, data) {
    return await AppDataService.patch(`visit/${id}`, data);
  }
  static async getAllMasterCode(apIQuery) {
    return await AppDataService.get(apIQuery);
  }
  static async getMasterDataByType({
    type,
    pageNumber = 1,
    limit = 100,
    searchValue = "",
  }) {
    const params = [
      `pageNumber=${pageNumber}`,
      `limit=${limit}`,
      `type=${type}`,
      searchValue ? `searchValue=${searchValue}` : "",
    ]
      .filter(Boolean)
      .join("&");
    return await AppDataService.get(`master-data?${params}`, {
      skipFacilityId: true,
    });
  }
  static async postMasterCode(data) {
    return await AppDataService.post(`mastercode`, data);
  }
  static async postVisitNoteData(data) {
    return await AppDataService.post(`visit-note`, data);
  }
  static async patchVisitNoteData(id, data) {
    return await AppDataService.patch(`visit-note/${id}`, data);
  }
  static async sendVisitNote(id, data) {
    return await AppDataService.post(`visit-note/${id}/send-note`, data);
  }
  static async postOrderData(data) {
    return await AppDataService.post(`order`, data);
  }
  static async postTransferPatient(data) {
    return await AppDataService.post(`disposition`, data);
  }

  static async postTransferEMSPatient(data) {
    return await AppDataService.post(`visit/ems/transfer`, data);
  }

  static async getDepartments() {
    return await AppDataService.get(`department`);
  }

  static async updateDispositionData(id, data) {
    return await AppDataService.patch(`disposition/${id}`, data);
  }
  static async deleteOrderItem(id) {
    return await AppDataService.delete(`order/${id}`);
  }
  static async getFacilities(data) {
    const queryParams = [
      "status=Active",
      data?.isBaseCamp ? "isBaseCamp=true" : "",
      data?.pageNumber >= 0 ? `pageNumber=${data.pageNumber}` : "",
      data?.limit ? `limit=${data.limit}` : "",
    ]
      .filter(Boolean)
      .join("&");

    const queryString = queryParams ? `?${queryParams}` : "";
    return await AppDataService.get(`facility${queryString}`);
  }
  static async patchVerifyOrderStatus(id, data) {
    return await AppDataService.patch(`order/${id}`, data);
  }

  static async patchPatient(id, data) {
    return await AppDataService.patch(`patient/${id}`, data);
  }

  static async postDataToJotForm(data) {
    return await AppDataService.post(`jot-form`, data);
  }
  static async getFacilityList(data) {
    const queryParams = [
      data?.pageNumber >= 0 ? `pageNumber=${data.pageNumber}` : "",
      data?.limit ? `limit=${data.limit}` : "",
    ]
      .filter(Boolean)
      .join("&");

    const queryString = queryParams ? `?${queryParams}` : "";
    return await AppDataService.get(`facility${queryString}`);
  }

  static async getRooms(data) {
    // Pass query via axios `params` (not a hand-built URL string). Building the
    // string manually collides with the global facility interceptor and produces
    // a malformed URL. `skipFacilityId` keeps the patient's facility we pass here.
    return await AppDataService.get(`room`, {
      params: {
        facilityId: data?.facilityId,
        pageNumber: data?.pageNumber,
        limit: data?.limit,
      },
      skipFacilityId: true,
    });
  }

  static async assignRoom(data) {
    return await AppDataService.post(`room/assign`, data);
  }

  static async sendEmail(data) {
    return await AppDataService.post(`notification/send/email`, data);
  }

  static async sendSMS(data) {
    return await AppDataService.post(`notification/send/sms`, data);
  }

  static async getPatientDocuments(patientId, data) {
    const queryParams = [data?.name ? `name=${data.name}` : ""]
      .filter(Boolean)
      .join("&");

    const queryString = queryParams ? `?${queryParams}` : "";
    return await AppDataService.get(`documents/${patientId}${queryString}`, {
      skipFacilityId: true,
    });
  }

  static async uploadPatientDocument(data) {
    return await AppDataService.post(`documents`, data);
  }

  static async deletePatientDocument(documentId) {
    return await AppDataService.delete(`documents/${documentId}`);
  }

  static async deleteVisit(visitId) {
    return await AppDataService.delete(`visit/${visitId}`);
  }

  static async downloadPatientDocument(documentId) {
    return await AppDataService.get(`documents/download/${documentId}`, {
      responseType: "blob",
    });
  }

  static async getOrderStatus(data) {
    const queryParams = [
      data?.pageNumber >= 0 ? `pageNumber=${data.pageNumber}` : "",
      data?.limit ? `limit=${data.limit}` : "",
    ]
      .filter(Boolean)
      .join("&");

    const queryString = queryParams ? `?${queryParams}` : "";
    return await AppDataService.get(`order/status${queryString}`);
  }

  static async getOrderTypes(data) {
    const queryParams = [
      data?.pageNumber >= 0 ? `pageNumber=${data.pageNumber}` : "",
      data?.limit ? `limit=${data.limit}` : "",
    ]
      .filter(Boolean)
      .join("&");

    const queryString = queryParams ? `?${queryParams}` : "";
    return await AppDataService.get(`order-type${queryString}`);
  }
}
